import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { SearchIcon, PackageIcon, TruckIcon, CreditCardIcon, RotateCcwIcon, ShieldCheckIcon, HeadphonesIcon, FileTextIcon, MessageCircleIcon, PhoneIcon, MailIcon, ChevronRightIcon } from 'lucide-react';
export function HelpCenterPage() {
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const categories = [{
    icon: PackageIcon,
    title: 'Orders & Tracking',
    description: 'Track orders, modify details, and manage deliveries',
    articles: 12,
    color: 'bg-blue-50 text-blue-600',
    href: '/faq#orders'
  }, {
    icon: TruckIcon,
    title: 'Shipping & Delivery',
    description: 'Delivery times, shipping fees, and coverage areas',
    articles: 8,
    color: 'bg-green-50 text-green-600',
    href: '/shipping-policy'
  }, {
    icon: CreditCardIcon,
    title: 'Payment Methods',
    description: 'Accepted payment options and billing information',
    articles: 6,
    color: 'bg-purple-50 text-purple-600',
    href: '/faq#payment'
  }, {
    icon: RotateCcwIcon,
    title: 'Returns & Refunds',
    description: 'Return policy, refund process, and timelines',
    articles: 10,
    color: 'bg-orange-50 text-orange-600',
    href: '/return-policy'
  }, {
    icon: ShieldCheckIcon,
    title: 'Account & Security',
    description: 'Manage account, password, and privacy settings',
    articles: 7,
    color: 'bg-red-50 text-red-600',
    href: '/faq#account'
  }, {
    icon: FileTextIcon,
    title: 'Prescriptions',
    description: 'Upload prescriptions, verification, and insurance',
    articles: 9,
    color: 'bg-cyan-50 text-cyan-600',
    href: '/faq#prescription'
  }];
  const popularArticles = [{
    title: 'How do I track my order?',
    category: 'Orders',
    views: '2.5k',
    href: '/faq'
  }, {
    title: 'What payment methods do you accept?',
    category: 'Payment',
    views: '1.8k',
    href: '/faq'
  }, {
    title: 'How long does delivery take?',
    category: 'Shipping',
    views: '1.6k',
    href: '/faq'
  }, {
    title: 'How do I return a product?',
    category: 'Returns',
    views: '1.4k',
    href: '/return-policy'
  }, {
    title: 'How do I upload my prescription?',
    category: 'Prescriptions',
    views: '1.2k',
    href: '/prescription'
  }];
  const contactOptions = [{
    icon: PhoneIcon,
    title: 'Phone Support',
    description: 'Available 24/7',
    action: 'Call: +966 920 000 000',
    color: 'text-blue-600',
    href: 'tel:+966920000000'
  }, {
    icon: MessageCircleIcon,
    title: 'Live Chat',
    description: 'Chat with our team',
    action: 'Start Chat',
    color: 'text-green-600',
    href: '#'
  }, {
    icon: MailIcon,
    title: 'Email Support',
    description: 'Response within 24 hours',
    action: 'support@innovapharmacy.com',
    color: 'text-purple-600',
    href: 'mailto:support@innovapharmacy.com'
  }];
  return <HelmetProvider data-id="element-7114" data-mp="1tg65y8852wze">
      <SEO title="Help Center" description="Find answers to your questions about orders, shipping, returns, payments, and more. Get help from Innova Pharmacy support team." canonical="/help-center" data-id="element-7115" data-mp="1tfo2orn27ihz" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7116" data-mp="1khu8pncrp0xz">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-7117" data-mp="1ojfeypusqgz7">
          <AnnouncementBar data-id="element-7118" data-mp="2c6f7rh00kmqk" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-7119" data-mp="2c88d8syw50wz" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-7120" data-mp="23evcj4ul4v0c" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-7121" data-mp="2v48bummdj2pj">
          {/* Mobile Header Overlay */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-7122" data-mp="oh9b95agxaq2">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-7123" data-mp="ofg0bdsto4c9" />
          </div>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Help Center'
        }]} title="Help Center" description="Find answers to your questions about orders, shipping, returns, payments, and more. Get help from Innova Pharmacy support team." data-id="element-7124" data-mp="t9aiudwoyd9r" />
          <div className="container mx-auto px-4 py-12" data-id="element-7125" data-mp="ajufzjxja8q6">
            {/* Categories Grid */}
            <div className="mb-12" data-id="element-7126" data-mp="1qu6k1p3gozvj">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-7127" data-mp="1lx6my3olb7gk">
                Browse by Category
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7128" data-mp="1t0wcde3v2ro6">
                {categories.map((category, index) => {
                const Icon = category.icon;
                return <a key={index} href={category.href} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all group" data-id="element-7129" data-mp="22zlvfa7hui60" data-mpi={index}>
                      <div className={`w-12 h-12 rounded-lg ${category.color} flex items-center justify-center mb-4`} data-id="element-7130" data-mp="1a7sh5oazw6rg">
                        <Icon className="w-6 h-6" data-id="element-7131" data-mp="1k68azz1epu4n" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#1E2B55]" data-id="element-7132" data-mp="1ozkuxvv55cr2">
                        {category.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-7133" data-mp="whyth9b87sjb">
                        {category.description}
                      </p>
                      <div className="flex items-center justify-between" data-id="element-7134" data-mp="39r71cc1o7n1m">
                        <span className="text-sm text-gray-500" data-id="element-7135" data-mp="35qn1v1tmk7cb">
                          {category.articles} articles
                        </span>
                        <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-[#1E2B55] transition-colors" data-id="element-7136" data-mp="30twm6flqfdrf" />
                      </div>
                    </a>;
              })}
              </div>
            </div>
            {/* Popular Articles */}
            <div className="mb-12" data-id="element-7137" data-mp="2m1cq1yx026jh">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-7138" data-mp="2i1byrdosy0v0">
                Popular Articles
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200" data-id="element-7139" data-mp="2d2r6q1jpenhn">
                {popularArticles.map((article, index) => <a key={index} href={article.href} className="flex items-center justify-between p-6 hover:bg-gray-50 transition-colors group" data-id="element-7140" data-mp="284z5ggx65p0s" data-mpi={index}>
                    <div className="flex-1" data-id="element-7141" data-mp="3vzkhbhawalcm">
                      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-[#1E2B55]" data-id="element-7142" data-mp="3vzaqxslxuc6e">
                        {article.title}
                      </h3>
                      <div className="flex items-center text-sm text-gray-500" data-id="element-7143" data-mp="3mycwo21axj10">
                        <span className="px-2 py-1 bg-gray-100 rounded-full mr-3" data-id="element-7144" data-mp="38ai3ylnanenx">
                          {article.category}
                        </span>
                        <span data-id="element-7145" data-mp="3d29ityx37vy8">{article.views} views</span>
                      </div>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-[#1E2B55] transition-colors flex-shrink-0 ml-4" data-id="element-7146" data-mp="2z7gxukyfhnk9" />
                  </a>)}
              </div>
            </div>
            {/* Contact Options */}
            <div data-id="element-7147" data-mp="2uaqhdwipopyb">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-7148" data-mp="2u95o0a84vd21">
                Still need help?
              </h2>
              <p className="text-center text-gray-600 mb-8" data-id="element-7149" data-mp="2aqro62u1feya">
                Our support team is available 24/7 to assist you
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-7150" data-mp="2kn4q1nzqkz44">
                {contactOptions.map((option, index) => {
                const Icon = option.icon;
                return <a key={index} href={option.href} className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-[#1E2B55] hover:shadow-lg transition-all text-center group" data-id="element-7151" data-mp="2tm8aavan3kk7" data-mpi={index}>
                      <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-50 transition-colors" data-id="element-7152" data-mp="2ydzr4e3zubx5">
                        <Icon className={`w-8 h-8 ${option.color}`} data-id="element-7153" data-mp="3d95xx3awyerj" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-7154" data-mp="38amaeygo8ux2">
                        {option.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-7155" data-mp="pgs7y2fx4yzt">
                        {option.description}
                      </p>
                      <span className="text-sm font-medium text-[#1E2B55] group-hover:underline" data-id="element-7156" data-mp="ki78i58hqjcf">
                        {option.action}
                      </span>
                    </a>;
              })}
              </div>
            </div>
            {/* Additional Help */}
            <div className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8 text-center" data-id="element-7157" data-mp="188bbcaduuf2d">
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-7158" data-mp="1443n0ebb714o">
                Can't find what you're looking for?
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-7159" data-mp="3ovvulj4juu5s">
                Report an issue and our team will investigate and get back to
                you
              </p>
              <a href="/report-issue" className="inline-block bg-[#1E2B55] text-white font-bold px-8 py-3 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-7160" data-mp="3jz5kdcffessa">
                Report an Issue
              </a>
            </div>
          </div>
        </main>
        <Footer data-id="element-7161" data-mp="28dlu5nd6s1gy" />
        <MobileFooterNav data-id="element-7162" data-mp="2d6ew63h9svpc" />
        <PharmacistChat data-id="element-7163" data-mp="2d6op9zxm3iz9" />
        <div className="h-16 md:hidden" data-id="element-7164" data-mp="2d784v9zshva4"></div>
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-7165" data-mp="2i5i2rscwf9zf" />
      </div>
    </HelmetProvider>;
}